// @ts-nocheck
import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
const uuidv4 = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, Loader2, X, ArrowLeft, ArrowRight } from 'lucide-react';

const AGE_GROUPS = ['10U', '11U', '12U', '13U', '14U', '15U', '16U', '17U', '18U', 'Adult'];

const schema = z.object({
  canonical_name: z.string().trim().min(3, 'At least 3 characters').max(100, 'Max 100 characters'),
  age_group: z.string().min(1, 'Required'),
  gender: z.enum(['boys', 'girls', 'coed'], { required_error: 'Required' }),
  home_state: z
    .string()
    .optional()
    .refine((v) => !v || /^[A-Za-z]{2}$/.test(v), 'Must be a 2-letter state code'),
  home_city: z.string().max(60, 'Max 60 characters').optional(),
  primary_color: z.string().optional(),
});

export interface TeamDetailsValue {
  canonical_name: string;
  age_group: string;
  gender: 'boys' | 'girls' | 'coed' | '';
  home_state: string;
  home_city: string;
  primary_color: string;
  logo_file: File | null;
  logo_url: string | null;
}

interface Props {
  value: TeamDetailsValue;
  onChange: (patch: Partial<TeamDetailsValue>) => void;
  onBack: () => void;
  onContinue: () => void;
}

export default function TeamDetailsForm({ value, onChange, onBack, onContinue }: Props) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      canonical_name: value.canonical_name,
      age_group: value.age_group,
      gender: value.gender || undefined,
      home_state: value.home_state,
      home_city: value.home_city,
      primary_color: value.primary_color || '#002E62',
    },
  });

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File must be under 2MB');
      return;
    }
    if (!user) {
      toast.error('Please sign in to upload');
      return;
    }

    onChange({ logo_file: file });
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${user.id}/${uuidv4()}.${ext}`;
      const { error } = await supabase.storage.from('team-logos').upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });
      if (error) {
        if (error.message?.toLowerCase().includes('bucket') || (error as any).statusCode === '404') {
          toast.warning('Logo upload unavailable — please configure the team-logos storage bucket');
        } else {
          toast.error(error.message);
        }
        return;
      }
      const { data: pub } = supabase.storage.from('team-logos').getPublicUrl(path);
      onChange({ logo_url: pub.publicUrl });
      toast.success('Logo uploaded');
    } catch (e: any) {
      toast.warning('Logo upload unavailable — please configure the team-logos storage bucket');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = (data: any) => {
    onChange({
      canonical_name: data.canonical_name.trim(),
      age_group: data.age_group,
      gender: data.gender,
      home_state: (data.home_state || '').toUpperCase(),
      home_city: data.home_city || '',
      primary_color: data.primary_color || '',
    });
    onContinue();
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="canonical_name">Team name *</Label>
        <Input id="canonical_name" {...form.register('canonical_name')} />
        {form.formState.errors.canonical_name && (
          <p className="text-sm text-destructive">{form.formState.errors.canonical_name.message as string}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Age group *</Label>
          <Select
            value={form.watch('age_group')}
            onValueChange={(v) => form.setValue('age_group', v, { shouldValidate: true })}
          >
            <SelectTrigger><SelectValue placeholder="Select age group" /></SelectTrigger>
            <SelectContent>
              {AGE_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          {form.formState.errors.age_group && (
            <p className="text-sm text-destructive">{form.formState.errors.age_group.message as string}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Gender *</Label>
          <Select
            value={form.watch('gender') as string}
            onValueChange={(v) => form.setValue('gender', v as any, { shouldValidate: true })}
          >
            <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="boys">Boys</SelectItem>
              <SelectItem value="girls">Girls</SelectItem>
              <SelectItem value="coed">Coed</SelectItem>
            </SelectContent>
          </Select>
          {form.formState.errors.gender && (
            <p className="text-sm text-destructive">{form.formState.errors.gender.message as string}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2 md:col-span-1">
          <Label htmlFor="home_state">Home state</Label>
          <Input
            id="home_state"
            maxLength={2}
            placeholder="CA"
            {...form.register('home_state')}
            onChange={(e) => form.setValue('home_state', e.target.value.toUpperCase())}
            value={(form.watch('home_state') || '').toUpperCase()}
          />
          {form.formState.errors.home_state && (
            <p className="text-sm text-destructive">{form.formState.errors.home_state.message as string}</p>
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="home_city">Home city</Label>
          <Input id="home_city" {...form.register('home_city')} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="primary_color">Primary color</Label>
        <div className="flex items-center gap-3">
          <input
            id="primary_color"
            type="color"
            className="h-10 w-16 rounded border cursor-pointer"
            {...form.register('primary_color')}
          />
          <span className="text-sm text-muted-foreground">{form.watch('primary_color')}</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Team logo</Label>
        <Card
          className={`border-2 border-dashed transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) handleFile(f);
          }}
        >
          <CardContent className="p-6">
            {value.logo_url ? (
              <div className="flex items-center gap-4">
                <img src={value.logo_url} alt="Logo" className="h-20 w-20 object-contain rounded border" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Logo uploaded</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onChange({ logo_url: null, logo_file: null })}
                  >
                    <X className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center py-6 cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
                ) : (
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                )}
                <p className="text-sm text-muted-foreground">
                  {uploading ? 'Uploading…' : 'Drag and drop, or click to upload'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">PNG/JPG up to 2MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to search
        </Button>
        <Button type="submit">
          Continue <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}
